import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { getMyProfile, UserProfile } from '../api/profile';
import { authService } from '../api/auth';
import { useSignOut } from '../store/signOut';
import { RootState } from '../../../store/store';

/**
 * TCH-16 — Settings (stories S10.2 / S10.4).
 *
 * The header has always linked here; the route simply did not exist, so it hit
 * the catch-all 404. This is the T0 scope: what is actually wired end to end.
 *
 * Notification preferences are deliberately NOT rendered. No endpoint stores
 * them, and shipping switches that silently discard the user's choice is the
 * same failure as the hardcoded "3 messages" badge. The full information
 * architecture for this screen is product decision TCH-D06.
 */
const Settings: React.FC = () => {
  const authUser = useSelector((state: RootState) => state.auth.user);
  const signOut = useSignOut();

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [sendingReset, setSendingReset] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    getMyProfile()
      .then((me) => {
        if (!cancelled) setProfile(me);
      })
      .catch(() => {
        /* Non-fatal: the page still offers sign-out and password reset. */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const email = profile?.email ?? authUser?.email ?? '';

  const handlePasswordReset = async () => {
    if (!email) {
      setError('No pudimos determinar tu correo. Vuelve a iniciar sesión.');
      return;
    }
    setError('');
    setNotice('');
    setSendingReset(true);
    try {
      await authService.forgotPassword({ email });
      setNotice(
        `Te enviamos un código a ${email}. Úsalo para elegir una contraseña nueva. Revisa también tu carpeta de spam.`,
      );
    } catch (err: any) {
      setError(err?.message || 'No pudimos enviar el código. Inténtalo de nuevo.');
    } finally {
      setSendingReset(false);
    }
  };

  const handleSignOut = async () => {
    // PRT-17-FR-01 — the shared routine: revoke server-side, clear locally
    // whatever happened, and tell the sign-in screen when the server could not
    // confirm. The flag only stops a second press while it works.
    setSigningOut(true);
    await signOut();
  };

  return (
    <div style={{ maxWidth: 640, margin: '0 auto' }}>
      <h1 style={{ marginBottom: 4 }}>Configuración</h1>
      <p style={{ color: '#666', marginTop: 0 }}>Tu cuenta, seguridad y sesión.</p>

      {error && (
        <div role="alert" style={alertStyle('#ffe9e9', '#cb4242', '#8f2323')}>
          {error}
        </div>
      )}
      {notice && <div style={alertStyle('#edf8e5', '#5ba617', '#3f7410')}>{notice}</div>}

      <Section title="Cuenta">
        <Row label="Correo" value={email || '—'} />
        <p style={{ color: '#666', fontSize: 14, margin: '12px 0 0' }}>
          Tu nombre y zona horaria se editan en <Link to="/profile">Mi perfil</Link>.
        </p>
      </Section>

      <Section title="Seguridad">
        <p style={{ color: '#666', fontSize: 14, margin: '0 0 12px' }}>
          Te enviaremos un código por correo para elegir una contraseña nueva.
        </p>
        <button className="btn btn-outline" onClick={handlePasswordReset} disabled={sendingReset}>
          {sendingReset ? 'Enviando…' : 'Cambiar mi contraseña'}
        </button>
        {notice && (
          <div style={{ marginTop: 10 }}>
            <Link to="/reset-password" state={{ email }}>
              Ya tengo el código →
            </Link>
          </div>
        )}
      </Section>

      <Section title="Sesión">
        <button className="btn btn-secondary" onClick={handleSignOut} disabled={signingOut}>
          {signingOut ? 'Cerrando…' : 'Cerrar sesión'}
        </button>
      </Section>

      <Section title="Ayuda">
        <p style={{ color: '#666', fontSize: 14, margin: 0 }}>
          ¿Necesitas ayuda con una sesión, un pago o tu cuenta? Escríbenos a{' '}
          <a href="mailto:support@iqmaxx.com">support@iqmaxx.com</a> y te respondemos.
        </p>
      </Section>
    </div>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section
    style={{
      border: '1px solid #d1d5da',
      borderRadius: 8,
      padding: 16,
      marginBottom: 16,
      background: '#fff',
    }}
  >
    <h2 style={{ fontSize: 16, margin: '0 0 12px' }}>{title}</h2>
    {children}
  </section>
);

const Row: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      gap: 12,
      flexWrap: 'wrap',
      fontSize: 14,
    }}
  >
    <span style={{ color: '#666' }}>{label}</span>
    <span style={{ fontWeight: 600 }}>{value}</span>
  </div>
);

function alertStyle(bg: string, border: string, fg: string): React.CSSProperties {
  return {
    background: bg,
    border: `1px solid ${border}`,
    color: fg,
    borderRadius: 8,
    padding: '10px 14px',
    marginBottom: 16,
    fontSize: 14,
  };
}

export default Settings;
